import { DropdownField, TanstackFieldWrapper } from 'components/form-fields-2';
import { Button, ComboBox, InlineLoading, Layer } from '@carbon/react';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import { LaunchLink } from 'components/launch-link';
import {
  CheckmarkFilled as CheckmarkIcon,
  ErrorFilled as ErrorIcon,
  Save,
} from '@carbon/react/icons';
import commonStyles from './common.module.scss';
import styles from './area-configs.module.scss';
import { ConfigData, useScenarioForm } from './contexts';
import { capitalize, debounce } from 'lodash-es';
import { DevelopmentType } from 'types/dca-scenarios-api/responses';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ConfigSkeleton } from './config-skeleton';
import {
  createAreaGeometryCategoriesAtom,
  createAreaGeometryDetailsSearchAtom,
  AreaGeometryResult,
  validateAreaConfigAtom,
  areaConfigDataAtom,
} from './atoms';
import classNames from 'classnames';
import { useLocation, useParams } from 'react-router-dom';
import { createResourceAtom } from 'lib/atoms';
import { AreaFormationResponse } from './types';

interface AreaGeometryDetailResult extends AreaGeometryResult {
  geometry: object | null;
}

const FALLBACK_ATOM = atom(null);
const UNPROCESSED = Symbol('unprocessed');

interface AreaConfigsProps {
  setIsAddScenarioNameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AreaConfigs = ({
  setIsAddScenarioNameModalOpen,
}: AreaConfigsProps) => {
  const {
    form,
    selectedConfig,
    setFieldBaseline,
    setConfigs,
    configs,
    isFormModified,
    handleSubmitForm,
    validConfigState,
    setValidConfigState,
    updateFieldDefault,
    setEnablePopulateConfig,
    saveCount,
  } = useScenarioForm();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedAreaGeometry, setSelectedAreaGeometry] =
    useState<AreaGeometryResult | null>(null);
  const [selectedPopulateConfig, setSelectedPopulateConfig] =
    useState<ConfigData | null>(null);

  const [enableValidation, setEnableValidation] = useState(false);

  const { pathname } = useLocation();
  const { id: scenarioId = null } = useParams<{ id?: string }>();

  const [validateAreaConfigResponse, validateAreaConfig] = useAtom(
    validateAreaConfigAtom
  );

  const [categories, setCategories] = useState<string[]>([]);

  const categoriesAtoms = useMemo(() => createAreaGeometryCategoriesAtom(), []);
  const areaSearchAtoms = useMemo(
    () => createAreaGeometryDetailsSearchAtom(),
    []
  );

  const formationValue = selectedConfig?.formation ?? null;

  const areaFormationAtom = useMemo(() => {
    if (!selectedAreaGeometry?.id) return FALLBACK_ATOM;

    return createResourceAtom<AreaFormationResponse>(
      `/area_geometries/${selectedAreaGeometry.id}/formations`
    );
  }, [selectedAreaGeometry?.id]);

  const areaFormationData = useAtomValue(areaFormationAtom);

  const areaGeometryHref =
    selectedConfig?.resources?.areaGeometry?.href ?? null;

  const areaGeometryAtom = useMemo(() => {
    if (!areaGeometryHref) return FALLBACK_ATOM;
    return createResourceAtom<AreaGeometryResult>(areaGeometryHref);
  }, [areaGeometryHref]);
  const areaGeometryData = useAtomValue(areaGeometryAtom);

  // Fetch area geometry detail (with geoJson) when an area is selected
  const areaGeometryDetailAtom = useMemo(() => {
    if (!selectedAreaGeometry?.href) return FALLBACK_ATOM;
    return createResourceAtom<AreaGeometryDetailResult>(
      selectedAreaGeometry.href
    );
  }, [selectedAreaGeometry?.href]);
  const areaGeometryDetail = useAtomValue(areaGeometryDetailAtom);

  // When area geometry detail loads, update the config's geoJson for the map
  useEffect(() => {
    if (!areaGeometryDetail?.data || 'error' in areaGeometryDetail.data) return;
    if (!selectedConfig?.id) return;

    const detail = areaGeometryDetail.data;
    const configId = selectedConfig.id;

    setConfigs((prev) =>
      prev.map((config) => {
        if (config.id !== configId) return config;
        return {
          ...config,
          areaGeometryGeoJson: detail.geometry ?? null,
        };
      })
    );
  }, [areaGeometryDetail?.data, selectedConfig?.id, setConfigs]);

  const [categoriesAtomValue] = useAtom(categoriesAtoms.searchAtom);
  const [areaSearchValue, setAreaSearchPayload] = useAtom(
    areaSearchAtoms.searchAtom
  );
  const getAreaConfigDataAtom = useSetAtom(areaConfigDataAtom);

  const previousFacetsRef = useRef<string>('');
  const selectedConfigRef = useRef(selectedConfig);
  selectedConfigRef.current = selectedConfig;

  const lastProcessedAreaGeometryRef = useRef<unknown>(UNPROCESSED);

  const formations = useMemo(() => {
    if (!areaFormationData?.data) return [];

    if ('error' in areaFormationData.data) return [];

    return areaFormationData.data.results;
  }, [areaFormationData]);

  useEffect(() => {
    if (saveCount > 0) {
      setSelectedPopulateConfig(null);
    }
  }, [saveCount]);

  useEffect(
    () => {
      const config = selectedConfigRef.current;
      if (!config) return;

      if (areaGeometryData?.data) {
        if ('error' in areaGeometryData.data) return;

        if (lastProcessedAreaGeometryRef.current === areaGeometryData.data)
          return;
        lastProcessedAreaGeometryRef.current = areaGeometryData.data;

        const category = areaGeometryData.data.category ?? '';
        setSelectedCategory(category);
        setSelectedAreaGeometry(areaGeometryData.data);
        setAreaSearchPayload((current) => ({
          ...current,
          category: category && category !== 'All' ? category : undefined,
          page: 1,
          pageSize: 200,
        }));

        setFieldBaseline('areaConfigs', {
          areaGeometryId: String(areaGeometryData.data.id),
          developmentType: config.developmentType,
          formation: formationValue ?? null,
          areaGeometryName: areaGeometryData.data.name,
        });
      } else if (areaGeometryData === null) {
        if (lastProcessedAreaGeometryRef.current === null) return;
        lastProcessedAreaGeometryRef.current = null;
        setSelectedAreaGeometry(null);

        setFieldBaseline('areaConfigs', {
          areaGeometryId: null,
          developmentType: 'ALL',
          formation: null,
          areaGeometryName: null,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      areaGeometryData,
      selectedConfig?.id,
      setFieldBaseline,
      setAreaSearchPayload,
    ]
  );

  useEffect(
    () => {
      if (!pathname) return;

      if (
        !selectedAreaGeometry?.id ||
        !selectedConfig?.developmentType ||
        !enableValidation
      )
        return;

      const areaGeometryId = selectedAreaGeometry?.id as unknown as string;
      const developmentType = selectedConfig?.developmentType;
      const formation = selectedConfig?.formation;

      validateAreaConfig({
        url: `${pathname}/validate_area_config`,
        type: 'POST',
        data: {
          areaGeometryId,
          developmentType,
          formation: formation ?? undefined,
        },
        onSuccess: (data) => {
          setValidConfigState({
            isValid: data.valid,
            message: data.message,
          });
        },
        onError: (error) => {
          console.log(error);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedAreaGeometry?.id,
      selectedConfig?.developmentType,
      selectedConfig?.formation,
      pathname,
      validateAreaConfig,
      setValidConfigState,
    ]
  );

  useEffect(() => {
    if (!categoriesAtomValue.data?.facets) return;

    const facetsHash = JSON.stringify(categoriesAtomValue.data.facets);
    if (facetsHash === previousFacetsRef.current) return;
    previousFacetsRef.current = facetsHash;

    const facets = categoriesAtomValue.data.facets as Record<string, any>;
    const categoryFacet = facets?.category;
    if (categoryFacet?.buckets) {
      const categoryValues = categoryFacet.buckets
        .map((b: { value: string }) => b.value)
        .filter((v: string) => v && v.trim() !== '')
        .sort();
      setCategories(categoryValues);
    }
  }, [categoriesAtomValue.data?.facets]);

  const resolvedAreaGeometry: AreaGeometryResult | null = useMemo(() => {
    if (!areaGeometryData?.data || 'error' in areaGeometryData.data)
      return null;
    return areaGeometryData.data;
  }, [areaGeometryData?.data]);

  const areaGeometries: AreaGeometryResult[] = useMemo(() => {
    if (!selectedCategory) {
      return resolvedAreaGeometry ? [resolvedAreaGeometry] : [];
    }

    const options = [...(areaSearchValue.data?.results ?? [])];

    if (resolvedAreaGeometry) {
      options.push(resolvedAreaGeometry);
    }

    return options;
  }, [areaSearchValue.data?.results, resolvedAreaGeometry, selectedCategory]);

  const handleCategoryChange = useCallback(
    (value: string | null) => {
      setSelectedCategory(value);
      setSelectedAreaGeometry(null);

      setAreaSearchPayload((current) => ({
        ...current,
        category: value && value !== 'All' ? value : undefined,
        page: 1,
        pageSize: 200,
      }));
    },
    [setAreaSearchPayload, setSelectedCategory, setSelectedAreaGeometry]
  );

  const categoryItems = useMemo(() => ['All', ...categories], [categories]);

  const debouncedTextSearch = useMemo(
    () =>
      debounce((text: string) => {
        setAreaSearchPayload((current) => ({
          ...current,
          text: text || undefined,
          page: 1,
        }));
      }, 350),
    [setAreaSearchPayload]
  );

  useEffect(() => {
    return () => {
      debouncedTextSearch.cancel();
    };
  }, [debouncedTextSearch]);

  const isGlobal =
    selectedConfig?.id === 'local' && selectedConfig?.type === 'global';

  const areaConfigsTitle = useMemo(() => {
    if (!selectedConfig?.areaGeometryName) return null;

    const name = `${selectedConfig.areaGeometryName} / ${capitalize(
      selectedConfig.developmentType
    )}${selectedConfig.formation ? ` / ${selectedConfig.formation}` : ''}`;
    return name;
  }, [
    selectedConfig?.areaGeometryName,
    selectedConfig?.developmentType,
    selectedConfig?.formation,
  ]);

  const handlePopulateConfig = (refConfig: ConfigData) => {
    getAreaConfigDataAtom({
      payload: {
        generalConfigUrl: refConfig.generalConfig?.href ?? null,
        segmentationConfigUrl: refConfig.segmentationConfig?.href ?? null,
        outlierConfigUrl: refConfig.outlierConfig?.href ?? null,
        productionStreamConfigsUrl:
          refConfig.resources?.productionStreamConfigs?.href ?? null,
      },
      onSuccess: (data) => {
        if (data.generalConfig) {
          form.setFieldValue('generalConfig', {
            ...data.generalConfig,
            id: refConfig.generalConfig?.id ?? null,
          });
          updateFieldDefault('generalConfig', {
            ...data.generalConfig,
            id: refConfig.generalConfig?.id ?? null,
          });
        }

        if (data.segmentationConfig) {
          form.setFieldValue('segmentationConfig', {
            ...data.segmentationConfig,
            id: refConfig.segmentationConfig?.id ?? null,
          });
          updateFieldDefault('segmentationConfig', {
            ...data.segmentationConfig,
            id: refConfig.segmentationConfig?.id ?? null,
          });
        }

        if (data.outlierConfig) {
          form.setFieldValue('outlierConfig', {
            ...data.outlierConfig,
            id: refConfig.outlierConfig?.id ?? null,
          });
          updateFieldDefault('outlierConfig', {
            ...data.outlierConfig,
            id: refConfig.outlierConfig?.id ?? null,
          });
        }

        if (data.productionStreamConfigs.length > 0) {
          const productionStreamConfigsWithoutId =
            data.productionStreamConfigs.map((config) => ({
              ...config,
              id: null,
            }));

          form.setFieldValue(
            'oilStreamConfigs',
            productionStreamConfigsWithoutId.filter(
              (config) => config.stream === 'oil'
            )
          );
          form.setFieldValue(
            'gasStreamConfigs',
            productionStreamConfigsWithoutId.filter(
              (config) => config.stream === 'gas'
            )
          );
          form.setFieldValue(
            'waterStreamConfigs',
            productionStreamConfigsWithoutId.filter(
              (config) => config.stream === 'water'
            )
          );

          setEnablePopulateConfig(true);
        }
      },
    });
  };

  return (
    <div className={commonStyles.configFormContainer}>
      {!!areaConfigsTitle && (
        <h2 className={styles.areaConfigsTitle}>{areaConfigsTitle}</h2>
      )}
      <div className={styles.areaConfigsHeader}>
        <h2 className={commonStyles.configFormTitle}>Area Configs</h2>

        {validateAreaConfigResponse?.loading && <InlineLoading />}

        {!validateAreaConfigResponse?.loading && validConfigState && (
          <TooltipIcon
            align="start"
            className={classNames(styles.tooltipIcon, {
              [styles.errorIcon]: !validConfigState.isValid,
            })}
            definition={validConfigState.message}
          >
            {validConfigState.isValid ? <CheckmarkIcon /> : <ErrorIcon />}
          </TooltipIcon>
        )}
      </div>
      <form.Subscribe
        selector={(state) => {
          const areaConfigs = state.values.areaConfigs;
          const isModified = isFormModified(state.values);

          let canSubmit =
            isModified && state.canSubmit && !!state.values.generalConfig?.id;

          if (state.values.areaConfigs?.areaGeometryId) {
            canSubmit =
              canSubmit && (validConfigState ? validConfigState.isValid : true);
          }
          return {
            areaConfigs,
            canSubmit,
          };
        }}
      >
        {({ areaConfigs, canSubmit }) => {
          return (
            <>
              {(!areaConfigs || categoryItems.length === 1) && (
                <ConfigSkeleton className={styles.configSkeleton} id="Area" />
              )}
              {areaConfigs && categoryItems.length > 1 && (
                <>
                  <div className={styles.inputsContainer}>
                    <DropdownField
                      id="areaConfigs.categoryFilter"
                      label="Select an option"
                      titleText="Area Category"
                      items={categoryItems}
                      itemToString={(item: string | null) => item ?? ''}
                      value={selectedCategory ?? null}
                      onChange={handleCategoryChange}
                      disabled={isGlobal || categoryItems.length === 1}
                    />

                    <div style={{ gridRow: 2 }}>
                      <div
                        className={classNames(styles.comboBoxWrapper, {
                          [styles.shrinkComboBox]: selectedAreaGeometry,
                        })}
                      >
                        <Layer>
                          <ComboBox<AreaGeometryResult>
                            id="areaConfigs.areaGeometryId"
                            placeholder="Search areas..."
                            titleText="Area"
                            items={areaGeometries}
                            itemToString={(item) => item?.name ?? ''}
                            selectedItem={selectedAreaGeometry}
                            onChange={({ selectedItem: item }) => {
                              if (!item) return;

                              setSelectedAreaGeometry(item);

                              const newAreaGeometryId = String(item.id);
                              const newAreaGeometryName = item.name ?? null;

                              form.setFieldValue('areaConfigs', {
                                ...areaConfigs,
                                areaGeometryId: newAreaGeometryId,
                                areaGeometryName: newAreaGeometryName,
                                formation: null,
                              });
                              setEnableValidation(true);

                              setConfigs((prev) =>
                                prev.map((config) => {
                                  return config.id === selectedConfig?.id
                                    ? {
                                        ...config,
                                        areaGeometryId: newAreaGeometryId,
                                        areaGeometryName: newAreaGeometryName,
                                        formation: null,
                                      }
                                    : config;
                                })
                              );
                            }}
                            onInputChange={(text) => {
                              debouncedTextSearch(text ?? '');
                            }}
                            disabled={isGlobal || !selectedCategory}
                          />
                        </Layer>

                        {selectedAreaGeometry?.href && (
                          <div className={styles.launchButtonWrapper}>
                            <LaunchLink
                              to={selectedAreaGeometry.href}
                              className={styles.launchIcon}
                              label={`Open area geometry for ${selectedAreaGeometry.name} in a new tab`}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ gridRow: 2 }}>
                      <form.Field name={'areaConfigs.developmentType' as any}>
                        {(field) => (
                          <TanstackFieldWrapper<DevelopmentType> field={field}>
                            {(props) => (
                              <DropdownField
                                id="areaConfigs.developmentType"
                                label="Choose an option"
                                titleText="Development Type"
                                items={['ALL', 'HORIZONTAL', 'VERTICAL']}
                                itemToString={(item: string) =>
                                  capitalize(item)
                                }
                                {...props}
                                onChange={(value) => {
                                  const newDevelopmentType =
                                    value as DevelopmentType;
                                  props.onChange(newDevelopmentType);

                                  setConfigs((prev) =>
                                    prev.map((config) => {
                                      return config.id === selectedConfig?.id
                                        ? {
                                            ...config,
                                            developmentType: newDevelopmentType,
                                          }
                                        : config;
                                    })
                                  );
                                }}
                              />
                            )}
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    </div>

                    <div style={{ gridRow: 2 }}>
                      <form.Field name={'areaConfigs.formation' as any}>
                        {(field) => (
                          <TanstackFieldWrapper<string | null> field={field}>
                            {(props) => (
                              <DropdownField
                                id="areaConfigs.formation"
                                label="Choose an option"
                                titleText="Select a formation"
                                disabled={
                                  !areaConfigs?.areaGeometryId ||
                                  formations.length === 0
                                }
                                items={formations}
                                itemToString={(item: string) => item}
                                {...props}
                                onChange={(value) => {
                                  const newFormation = value;
                                  props.onChange(newFormation);

                                  setConfigs((prev) =>
                                    prev.map((config) => {
                                      return config.id === selectedConfig?.id
                                        ? {
                                            ...config,
                                            formation: newFormation,
                                          }
                                        : config;
                                    })
                                  );
                                }}
                              />
                            )}
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    </div>

                    <div style={{ gridRow: 2 }}>
                      <DropdownField
                        id="areaConfigs.populateFrom"
                        label="Choose an option"
                        titleText="Populate from existing configs (optional)"
                        items={configs.filter(
                          (config) =>
                            config.href && config.id !== selectedConfig?.id
                        )}
                        itemToString={(item: any) => {
                          const name = `${
                            item.areaGeometryName || 'Global'
                          } / ${capitalize(item.developmentType)}${
                            item.formationName ? ` / ${item.formationName}` : ''
                          }`;
                          return name;
                        }}
                        value={selectedPopulateConfig}
                        onChange={(value) => {
                          handlePopulateConfig(value);
                          setSelectedPopulateConfig(value);
                        }}
                      />
                    </div>
                  </div>

                  <div className={commonStyles.buttonContainer}>
                    <Button
                      size="md"
                      kind="primary"
                      renderIcon={Save}
                      disabled={
                        !canSubmit || validateAreaConfigResponse?.loading
                      }
                      onClick={() => {
                        if (!scenarioId) {
                          setIsAddScenarioNameModalOpen(true);
                        } else {
                          handleSubmitForm({
                            dcaScenarioId: scenarioId,
                            areaConfigId: selectedConfig?.href
                              ? selectedConfig.id
                              : null,
                          });
                        }
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </>
              )}
            </>
          );
        }}
      </form.Subscribe>
    </div>
  );
};
